/**
 * Custom "Pickup location" filter (Made to Order / Athens / Kifisia).
 * Shopify can't filter on these, so we load every product of the currently
 * filtered collection from ?view=availability and filter the grid client-side.
 * When a Storefront API token is configured (window.availabilityFilterConfig),
 * the store tokens come from real per-location stock instead of the metafield.
 */
(() => {
  const PARAM = 'avail';
  const STORES = { athens: 'Απόλλωνος', kifisia: 'Κολοκοτρώνη' };
  const BATCH_SIZE = 50;
  const cache = new Map();
  const STORAGE_KEY = 'availability-filter:stores';
  const STORAGE_TTL = 5 * 60 * 1000;
  const storeCache = new Map();

  // Keep store stock for a few minutes across page loads so filtering feels instant.
  try {
    const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    if (saved && Date.now() - saved.time < STORAGE_TTL) {
      Object.entries(saved.stores).forEach(([id, stores]) => storeCache.set(id, stores));
    }
  } catch (error) {}

  const saveStoreCache = () => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ time: Date.now(), stores: Object.fromEntries(storeCache) }));
    } catch (error) {}
  };

  const STORE_QUERY = `query StoreStock($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        id
        variants(first: 250) {
          nodes {
            storeAvailability(first: 5) {
              nodes { quantityAvailable location { name } }
            }
          }
        }
      }
    }
  }`;

  const fetchStoreTokens = async (ids, config) => {
    const response = await fetch(`/api/${config.apiVersion}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': config.storefrontToken,
      },
      body: JSON.stringify({ query: STORE_QUERY, variables: { ids } }),
    });
    const { data, errors } = await response.json();
    if (errors) throw new Error(errors.map((error) => error.message).join('; '));

    data.nodes.forEach((product) => {
      if (!product) return;
      const found = new Set();
      product.variants.nodes.forEach((variant) => {
        variant.storeAvailability.nodes.forEach(({ quantityAvailable, location }) => {
          if (quantityAvailable <= 0) return;
          Object.entries(STORES).forEach(([token, name]) => {
            if (location.name.includes(name)) found.add(token);
          });
        });
      });
      storeCache.set(product.id, [...found]);
    });
  };

  // Replaces the metafield-based store tokens with ones from real stock per location.
  const applyStoreStock = async (items) => {
    const config = window.availabilityFilterConfig;
    if (!config || !config.storefrontToken) return;

    const gid = (item) => `gid://shopify/Product/${item.dataset.productId}`;
    const missing = [...new Set(items.map(gid))].filter((id) => !storeCache.has(id));
    const batches = [];
    for (let i = 0; i < missing.length; i += BATCH_SIZE) batches.push(missing.slice(i, i + BATCH_SIZE));
    await Promise.all(batches.map((ids) => fetchStoreTokens(ids, config)));
    if (batches.length) saveStoreCache();

    items.forEach((item) => {
      const base = tokens(item).filter((token) => !(token in STORES));
      item.dataset.avail = [...base, ...(storeCache.get(gid(item)) || [])].join(' ');
    });
  };

  const getSelected = () => new URL(window.location.href).searchParams.getAll(PARAM);

  const buildViewUrl = () => {
    const url = new URL(window.location.href);
    [PARAM, 'page', 'section_id'].forEach((key) => url.searchParams.delete(key));
    url.searchParams.set('view', 'availability');
    return url;
  };

  const fetchPage = async (url, page) => {
    const pageUrl = new URL(url);
    pageUrl.searchParams.set('page', page);
    const html = await (await fetch(pageUrl)).text();
    return new DOMParser().parseFromString(html, 'text/html');
  };

  const loadItems = (url) => {
    const key = url.toString();
    if (!cache.has(key)) {
      cache.set(
        key,
        (async () => {
          const first = await fetchPage(url, 1);
          const root = first.querySelector('[data-availability-pages]');
          const pages = root ? parseInt(root.dataset.availabilityPages, 10) || 1 : 1;
          const rest = await Promise.all(
            Array.from({ length: pages - 1 }, (_, i) => fetchPage(url, i + 2))
          );
          return [first, ...rest].flatMap((doc) => Array.from(doc.querySelectorAll('[data-avail]')));
        })().catch((error) => {
          cache.delete(key);
          throw error;
        })
      );
    }
    return cache.get(key);
  };

  function tokens(item) {
    return item.dataset.avail.trim().split(/\s+/).filter(Boolean);
  }

  const renderActiveChips = (selected) => {
    const active = document.getElementById('FacetFiltersActive');
    if (!active) return;
    active.querySelectorAll('[data-avail-chip]').forEach((chip) => chip.remove());

    selected.forEach((value) => {
      const input = document.querySelector(`input[data-avail-input][value="${value}"]`);
      if (!input) return;
      const label = input.labels[0].firstChild.textContent.trim();
      const chip = document.createElement('a');
      chip.href = '#';
      chip.dataset.availChip = value;
      chip.className = 'facet-remove btn btn--secondary flex relative';
      chip.innerHTML = `<div class="flex items-center justify-between gap-3"><div class="facet-title text-wrap flex items-center gap-2"></div><span aria-hidden="true">&times;</span></div>`;
      chip.querySelector('.facet-title').textContent = label;
      chip.addEventListener('click', (event) => {
        event.preventDefault();
        const form = document.querySelector('form[is="facet-form"]');
        const target = form && form.querySelector(`input[data-avail-input][value="${value}"]`);
        if (!target) return;
        form.querySelectorAll(`input[data-avail-input][value="${value}"]`).forEach((el) => (el.checked = false));
        target.dispatchEvent(new Event('change', { bubbles: true }));
      });
      active.prepend(chip);
    });
  };

  const update = async () => {
    const inputs = document.querySelectorAll('input[data-avail-input]');
    if (!inputs.length) return;

    const selected = getSelected();
    inputs.forEach((input) => (input.checked = selected.includes(input.value)));
    renderActiveChips(selected);

    const container = document.getElementById('ProductGridContainer');
    if (selected.length && container) container.style.opacity = '0.3';

    let items;
    try {
      items = await loadItems(buildViewUrl());
    } catch (error) {
      if (container) container.style.opacity = '';
      console.error('[availability-filter]', error);
      return;
    }

    try {
      await applyStoreStock(items);
    } catch (error) {
      // Fall back to the metafield-based store tokens rendered by Liquid.
      console.error('[availability-filter] store stock lookup failed', error);
    }

    // Ignore stale responses if the URL changed while loading.
    if (getSelected().join() !== selected.join()) return;

    document.querySelectorAll('[data-avail-count]').forEach((el) => {
      const count = items.filter((item) => tokens(item).includes(el.dataset.availCount)).length;
      el.textContent = `(${count})`;
      const input = el.closest('li').querySelector('input');
      input.disabled = count === 0 && !input.checked;
    });

    if (!selected.length || !container) return;

    const matches = items.filter((item) => tokens(item).some((token) => selected.includes(token)));
    const list = document.getElementById('ProductsList');

    container.querySelectorAll('.pagination, .empty-state').forEach((el) => el.remove());
    if (list) list.replaceChildren(...matches.map((item) => document.importNode(item, true)));
    if (!matches.length) {
      const empty = document.createElement('div');
      empty.className = 'empty-state flex flex-col items-center justify-center gap-6 text-center';
      empty.innerHTML = '<p class="h4">No products found</p>';
      container.prepend(empty);
    }

    const productCount = document.getElementById('ProductCount');
    if (productCount) productCount.textContent = `${matches.length} ${matches.length === 1 ? 'product' : 'products'}`;

    container.style.opacity = '';
  };

  document.addEventListener('collection:rerendered', update);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', update);
  } else {
    update();
  }
})();
